/**
 * Georeferencing: our COGs are north-up, unrotated rasters in EPSG:3857
 * (spherical Web Mercator). geotiff.js resolves the ModelTiepoint +
 * ModelPixelScale tags into a bounding box for us (`getBoundingBox()`);
 * we only need to invert spherical Mercator to lon/lat for MapLibre's
 * `image` source, which wants corners in [lon, lat].
 */

export type LonLat = [number, number];

export interface ImageCorners {
  topLeft: LonLat;
  topRight: LonLat;
  bottomRight: LonLat;
  bottomLeft: LonLat;
}

/** EPSG:3857 uses a spherical Earth model with this radius. */
export const WEB_MERCATOR_R = 6378137;

export function mercatorToLonLat(x: number, y: number): LonLat {
  const lon = (x / WEB_MERCATOR_R) * (180 / Math.PI);
  const lat = (2 * Math.atan(Math.exp(y / WEB_MERCATOR_R)) - Math.PI / 2) * (180 / Math.PI);
  return [lon, lat];
}

/** Inverse of mercatorToLonLat — needed by the mosaic path to turn the MapLibre
 * viewport's lon/lat bounds into EPSG:3857 pixel coordinates on the mosaic grid
 * (mosaicGrid.ts). Station/composite frames never need this direction: their
 * corners are read directly off each COG's own tiepoint via geotiff.js. */
export function lonLatToMercator(lon: number, lat: number): [number, number] {
  const x = (lon * Math.PI * WEB_MERCATOR_R) / 180;
  const latRad = (lat * Math.PI) / 180;
  const y = WEB_MERCATOR_R * Math.log(Math.tan(Math.PI / 4 + latRad / 2));
  return [x, y];
}

/**
 * bbox = [minX, minY, maxX, maxY] in EPSG:3857 (geotiff.js `getBoundingBox()`).
 * Corners ordered for MapLibre's `image` source: top-left, top-right,
 * bottom-right, bottom-left (image row 0 = north edge).
 */
export function cornersFromBBox3857(bbox: [number, number, number, number]): ImageCorners {
  const [minX, minY, maxX, maxY] = bbox;
  return {
    topLeft: mercatorToLonLat(minX, maxY),
    topRight: mercatorToLonLat(maxX, maxY),
    bottomRight: mercatorToLonLat(maxX, minY),
    bottomLeft: mercatorToLonLat(minX, minY),
  };
}

export function cornersToMapLibreCoordinates(c: ImageCorners): [LonLat, LonLat, LonLat, LonLat] {
  return [c.topLeft, c.topRight, c.bottomRight, c.bottomLeft];
}
